import { useMemo, useState } from "react";
import { styles } from "../styles";
import {
  isTaxCertExpired,
  isEffectivelyTaxExempt,
} from "../utils/billTotals";

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

/**
 * Pick-which-lines modal for the Progress Bill / Final Bill flow on a job.
 *
 * Shows every UNBILLED line in the job's costing (filtered by
 * `billedOnInvoice == null`) grouped by category, with a checkbox per
 * line. Computes preview totals (subtotal + taxes + grand total) from
 * the selection in real time and respects the customer's tax-exempt
 * status (taxes force to $0 when exempt).
 *
 * Submitting calls onConfirm(billData) with:
 *   { type: "partial" | "final", description, lineItemIds, subtotal,
 *     taxableSubtotal, taxes, grandTotal }
 *
 * The caller (JobCosting) hands billData to actions.generateBill which
 * runs the INV# counter transaction + appends to job.bills + stamps the
 * included lines' billedOnInvoice.
 */
export default function ProgressBillModal({
  initialType = "partial", // "partial" | "final" — fixed by the button you clicked from JobCosting
  // Phase D: "line-item" | "percentage". Locked by the caller; the modal
  // doesn't let the user switch mid-flow. JobCosting decides which mode
  // is allowed based on job state (quoted vs T&M, what mode prior bills
  // used) and surfaces separate buttons per mode.
  billingMode = "line-item",
  job,
  customer,
  taxes = [],
  onCancel,
  onConfirm,
}) {
  // type is locked to initialType — the caller picks Partial vs Final by
  // which button they clicked in JobCosting. No in-modal toggle to keep
  // the workflow unambiguous (Justin feedback 2026-06-12).
  const type = initialType;
  const [description, setDescription] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  // Phase D percentage-mode state. quotedBase = the contracted price
  // (priceMode === "quoted" stamped at job creation by approve flows
  // or by the manual Quoted job-create path). percentAlreadyBilled is
  // the running sum of every prior non-voided percentage bill so the
  // picker can show remaining capacity.
  const c = job.costing || {};
  const quotedBase = num(c.quotedAmount);
  const existingBills = (job.bills || []).filter((b) => !b.voided);
  const percentAlreadyBilled = existingBills
    .filter((b) => b.billingMode === "percentage")
    .reduce((s, b) => s + (num(b.percent) || 0), 0);
  const percentRemaining = Math.max(0, 100 - percentAlreadyBilled);

  // Default the percentage field for the user. Final bill auto-fills the
  // remaining percent so admin can submit with one click; partial leaves
  // it empty so they enter the chunk they're billing right now.
  const [percentInput, setPercentInput] = useState(() => {
    if (billingMode !== "percentage") return "";
    return type === "final" ? String(percentRemaining) : "";
  });
  const [overrideAmount, setOverrideAmount] = useState("");

  // Pull every UNBILLED line out of each costing category along with its
  // computed billable amount + taxable flag so the modal can render +
  // tally with a single shared row component below.
  const rows = useMemo(() => {
    const isUnbilled = (line) => !line?.billedOnInvoice;
    const c = job.costing || {};
    const out = [];
    (c.labor || []).filter(isUnbilled).forEach((l) =>
      out.push({
        id: l.id,
        category: "Labor",
        label: `${l.description || "Labor"}${
          l.hours ? ` — ${num(l.hours)} hr` : ""
        }`,
        amount: num(l.hours) * num(l.rate),
        taxable: l.taxable !== false,
      })
    );
    (c.materials || []).filter(isUnbilled).forEach((m) =>
      out.push({
        id: m.id,
        category: "Materials",
        label: `${m.name || "Material"}${
          m.partNumber ? ` (#${m.partNumber})` : ""
        }${m.qty ? ` × ${num(m.qty)}` : ""}`,
        amount:
          num(m.qty) * num(m.unitCost) * (1 + num(m.markup) / 100),
        taxable: m.taxable !== false,
      })
    );
    (c.expenses || []).filter(isUnbilled).forEach((e) =>
      out.push({
        id: e.id,
        category: "Expenses",
        label: e.description || "Expense",
        amount: num(e.amount) * (1 + num(e.markup) / 100),
        taxable: e.taxable !== false,
      })
    );
    (c.fuel || []).filter(isUnbilled).forEach((f) => {
      const lineCost =
        f.mode === "flat"
          ? num(f.amount)
          : num(f.costPerMile) * num(f.milesOneWay) * 2 * num(f.trips);
      out.push({
        id: f.id,
        category: "Fuel / Trip",
        label:
          f.mode === "flat"
            ? f.description || "Trip charge"
            : `Trip charge — ${num(f.trips)} trip(s)`,
        amount: lineCost * (1 + num(f.markup) / 100),
        taxable: f.taxable !== false,
      });
    });
    return out;
  }, [job.costing]);

  const grouped = useMemo(() => {
    const byCat = new Map();
    for (const r of rows) {
      if (!byCat.has(r.category)) byCat.set(r.category, []);
      byCat.get(r.category).push(r);
    }
    return [...byCat.entries()];
  }, [rows]);

  // Running totals from the current selection. Tax respects per-line
  // taxable flag and the customer's tax-exempt status (zeros out
  // everything when exempt). Mirrors the JobCosting + mirrorInvoice logic.
  const selectedRows = rows.filter((r) => selectedIds.has(r.id));
  const lineSubtotal = selectedRows.reduce((s, r) => s + r.amount, 0);
  const lineTaxableSubtotal = selectedRows.reduce(
    (s, r) => s + (r.taxable ? r.amount : 0),
    0
  );

  // Phase D: percentage-mode totals. percent × quotedBase + nudge =
  // subtotal. The "nudge" field is an additive +/- adjustment so admin
  // can bill a non-round number without fudging the percent (Justin's
  // ask 2026-06-15: previously replaced the computed total, which made
  // "click up 1 cent" drop the bill from $6,000 to $0.01).
  // Percentage bills assume the whole subtotal is taxable — the
  // contracted price has its own tax treatment baked in.
  const percent = num(percentInput);
  const computedPctSubtotal = (percent / 100) * quotedBase;
  const nudgeAmount =
    overrideAmount !== "" && Number.isFinite(parseFloat(overrideAmount))
      ? parseFloat(overrideAmount)
      : 0;
  const pctSubtotal = Math.max(0, computedPctSubtotal + nudgeAmount);
  const pctTaxableSubtotal = pctSubtotal;

  const subtotal = billingMode === "percentage" ? pctSubtotal : lineSubtotal;
  const taxableSubtotal =
    billingMode === "percentage" ? pctTaxableSubtotal : lineTaxableSubtotal;

  // Effective exempt: taxExempt flag AND cert not expired. An expired
  // cert makes the exemption legally invalid, so tax IS applied until
  // renewal. The banner below renders on raw taxExempt with separate
  // messaging for the expired case.
  const customerExempt = isEffectivelyTaxExempt(customer);
  const taxesApplied = customerExempt
    ? []
    : taxes.map((t) => ({
        name: t.name || "Tax",
        mode: t.mode || "percent",
        rate: t.rate || "",
        amount:
          t.mode === "flat"
            ? num(t.amount)
            : taxableSubtotal * (num(t.rate) / 100),
      }));
  const taxTotal = taxesApplied.reduce((s, t) => s + t.amount, 0);
  const grandTotal = subtotal + taxTotal;

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(rows.map((r) => r.id)));
  const clearAll = () => setSelectedIds(new Set());

  // Phase D submit validation differs by mode. Line-item: existing rule
  // (description + at least one line). Percentage: description + percent
  // > 0 + percent + already-billed must not exceed 100. The nudge field
  // is an additive +/- adjustment so the percent cap still applies; a
  // negative nudge can also push subtotal below 0 — pctSubtotal clamps
  // to >= 0 already so submit just requires the final amount > 0.
  const percentValid =
    billingMode !== "percentage" ||
    (percent > 0 &&
      percent + percentAlreadyBilled <= 100.001 &&
      pctSubtotal > 0);
  const canSubmit =
    description.trim().length > 0 &&
    !submitting &&
    (billingMode === "percentage" ? percentValid : selectedIds.size > 0);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        type,
        description: description.trim(),
        subtotal,
        taxableSubtotal,
        taxes: taxesApplied,
        grandTotal,
        billingMode,
      };
      if (billingMode === "percentage") {
        payload.percent = percent;
        payload.percentBase = quotedBase;
      } else {
        payload.lineItemIds = [...selectedIds];
      }
      await onConfirm(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        overflowY: "auto",
        padding: "40px 20px",
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "20px",
          maxWidth: "720px",
          width: "100%",
          maxHeight: "calc(100vh - 80px)",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0 }}>
          {type === "final" ? "Generate Final Bill" : "Generate Progress Bill"}
          {billingMode === "percentage" && (
            <span
              style={{
                marginLeft: "8px",
                fontSize: "13px",
                padding: "2px 8px",
                background: "#e0eaff",
                color: "#1a5fb4",
                border: "1px solid #bcd0ee",
                borderRadius: "4px",
                fontWeight: 600,
                verticalAlign: "middle",
              }}
            >
              % of contracted price
            </span>
          )}
        </h2>
        {type === "final" && (
          <div
            style={{
              padding: "6px 10px",
              background: "#fff3cd",
              border: "1px solid #f0c674",
              borderRadius: "4px",
              fontSize: "12px",
              color: "#7a5400",
              marginBottom: "10px",
            }}
          >
            <strong>This closes the job.</strong> Pick the remaining
            unbilled line items to put on the final invoice. Once
            submitted, the job moves to Completed.
          </div>
        )}

        <div style={{ marginBottom: "12px" }}>
          <label style={{ fontSize: "13px", display: "block" }}>
            Bill description{" "}
            <span style={{ color: "#c33", fontSize: "11px" }}>(required)</span>
          </label>
          <input
            style={{ ...styles.input, width: "100%" }}
            placeholder="e.g. Equipment delivery — March 2026"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {customer?.taxExempt && (() => {
          const certExpired = isTaxCertExpired(customer);
          return (
            <div
              style={{
                marginBottom: "10px",
                padding: "6px 10px",
                background: certExpired ? "#fdecec" : "#fff8e0",
                border: certExpired ? "1px solid #e0a0a0" : "1px solid #f0d27a",
                borderRadius: "4px",
                fontSize: "12px",
                color: certExpired ? "#8a1f1f" : "#6a4900",
              }}
            >
              {certExpired ? (
                <>
                  <strong>TAX EXEMPT customer — cert expired.</strong>{" "}
                  Exemption is invalid until refreshed, so tax IS being
                  applied on this bill.
                  {customer?.taxExemptCert
                    ? ` Cert on file: ${customer.taxExemptCert}.`
                    : ""}
                </>
              ) : (
                <>
                  <strong>TAX EXEMPT customer</strong> — tax block on this
                  bill will show $0.00
                  {customer?.taxExemptCert
                    ? ` with cert ${customer.taxExemptCert}`
                    : ""}
                  .
                </>
              )}
              {certExpired && (
                <div style={{ marginTop: "4px", fontWeight: 600 }}>
                  ⚠ Cert expired {customer.taxExemptCertExpires} — verify
                  with customer and refresh to restore exemption.
                </div>
              )}
            </div>
          );
        })()}

        {billingMode === "percentage" ? (
          <div
            style={{
              marginBottom: "10px",
              padding: "10px",
              background: "#f3f8ff",
              border: "1px solid #cfdcf5",
              borderRadius: "6px",
              fontSize: "13px",
            }}
          >
            <div style={{ marginBottom: "6px" }}>
              Contracted price: <strong>{money(quotedBase)}</strong>
              {percentAlreadyBilled > 0 && (
                <>
                  {" "}
                  · Already billed:{" "}
                  <strong>{percentAlreadyBilled}%</strong> · Remaining:{" "}
                  <strong>{percentRemaining}%</strong>
                </>
              )}
            </div>
            <div style={{ marginBottom: "6px" }}>
              <label>Percent to bill: </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="0"
                style={{
                  ...styles.input,
                  width: "80px",
                  marginLeft: "4px",
                }}
                value={percentInput}
                onChange={(e) => setPercentInput(e.target.value)}
              />{" "}
              %
              <span style={{ marginLeft: "12px", color: "#666" }}>
                = {money(computedPctSubtotal)} of {money(quotedBase)}
              </span>
            </div>
            <div style={{ marginBottom: "6px", fontSize: "12px" }}>
              <label>Nudge ± $ (optional): </label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                style={{
                  ...styles.input,
                  width: "120px",
                  marginLeft: "4px",
                }}
                value={overrideAmount}
                onChange={(e) => setOverrideAmount(e.target.value)}
              />
              <span style={{ marginLeft: "8px", color: "#666" }}>
                Add (or subtract, with a minus) to the % total above
                without changing the percent on record. Example:
                +100 = bill ${" "}
                {(computedPctSubtotal + 100).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}{" "}
                instead.
              </span>
            </div>
            <div style={{ fontWeight: 600 }}>
              Bill total ={" "}
              <span style={{ color: "#1a5fb4" }}>{money(pctSubtotal)}</span>
            </div>
            {percent + percentAlreadyBilled > 100.001 && (
              <div style={{ color: "#b91c1c", fontWeight: 600 }}>
                This percentage would push total billed over 100%.
                Reduce the percent.
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "6px",
            }}
          >
            <strong style={{ fontSize: "13px" }}>
              Select line items ({rows.length} unbilled)
            </strong>
            <button
              style={{ ...styles.button, padding: "2px 8px", fontSize: "11px" }}
              onClick={selectAll}
              type="button"
            >
              Select all
            </button>
            <button
              style={{ ...styles.button, padding: "2px 8px", fontSize: "11px" }}
              onClick={clearAll}
              type="button"
            >
              Clear
            </button>
          </div>
        )}

        {billingMode === "percentage" ? null : rows.length === 0 ? (
          <div
            style={{
              padding: "10px",
              border: "1px dashed #ddd",
              borderRadius: "4px",
              fontSize: "12px",
              color: "#666",
            }}
          >
            No unbilled line items on this job. Add costing rows to the
            Estimated Bill first.
          </div>
        ) : (
          <div
            style={{
              border: "1px solid #eee",
              borderRadius: "4px",
              maxHeight: "300px",
              overflowY: "auto",
              padding: "6px 10px",
              marginBottom: "10px",
            }}
          >
            {grouped.map(([cat, list]) => (
              <div key={cat} style={{ marginBottom: "8px" }}>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#666",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    margin: "4px 0",
                  }}
                >
                  {cat}
                </div>
                {list.map((r) => (
                  <label
                    key={r.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "3px 0",
                      fontSize: "12px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                    <span style={{ flex: 1 }}>
                      {r.label}
                      {!r.taxable && (
                        <span
                          style={{
                            marginLeft: "6px",
                            fontSize: "10px",
                            color: "#888",
                          }}
                        >
                          (non-taxable)
                        </span>
                      )}
                    </span>
                    <span style={{ fontWeight: 600 }}>
                      {money(r.amount)}
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        )}

        <div
          style={{
            borderTop: "1px solid #ddd",
            paddingTop: "8px",
            fontSize: "13px",
            marginBottom: "10px",
          }}
        >
          <div>Subtotal: {money(subtotal)}</div>
          {customerExempt ? (
            <div style={{ fontSize: "12px", color: "#6a4900" }}>
              Sales tax — EXEMPT
              {customer?.taxExemptCert
                ? ` (Cert ${customer.taxExemptCert})`
                : ""}
              : {money(0)}
            </div>
          ) : (
            taxesApplied.map((t, i) => (
              <div key={i} style={{ fontSize: "12px", color: "#666" }}>
                {t.name}
                {t.mode === "percent" && t.rate ? ` (${num(t.rate)}%)` : ""}
                : {money(t.amount)}
              </div>
            ))
          )}
          <div style={{ fontWeight: "bold", fontSize: "15px" }}>
            Total Due: {money(grandTotal)}
          </div>
        </div>

        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <button
            style={styles.button}
            onClick={onCancel}
            disabled={submitting}
            type="button"
          >
            Cancel
          </button>
          <button
            style={{
              ...styles.button,
              background: canSubmit ? "#1a7f37" : "#aaa",
              color: "white",
            }}
            onClick={submit}
            disabled={!canSubmit}
            type="button"
          >
            {submitting
              ? "Generating…"
              : type === "final"
              ? "Generate Final Bill & Close Job"
              : "Generate Partial Bill"}
          </button>
        </div>
      </div>
    </div>
  );
}
